import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'

import { graphql } from '@/shared/graphql/router'
import { useMoveItemsMutation } from '@/shared/graphql/legacy/generated'
import type { Folder } from '@/shared/graphql/router/graphql'
import type { BreadcrumbItem } from '@dacr/library'
import { removeItemsFromExplorerCache } from '@/features/explorer/utils/explorerCache'
import { useExplorerSelection } from '@/features/explorer/composables/useExplorerSelection'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'

const FOLDERS_QUERY = graphql(`
  query MoveFolders($path: String!) {
    folderByPath(path: $path) {
      id
      name
      path
      items(input: { first: 100, filterBy: { type: FOLDER } }) {
        edges {
          node {
            ... on Folder {
              id
              name
              path
              totalFiles
            }
          }
        }
      }
    }
  }
`)

const CONFIRM_THRESHOLD = 50

export function useMoveItems(ids: string[], close: () => void) {
  const { clear: clearSelection } = useExplorerSelection()

  const folderPath = ref('/')
  const variables = computed(() => ({ path: folderPath.value }))

  const { result, loading } = useQuery(FOLDERS_QUERY, variables, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    clientId: 'router',
  })

  const { mutate: moveItems, loading: moveItemsLoading } =
    useMoveItemsMutation()

  const path = ref<{ name: string; id: string; path: string }[]>([
    { name: 'Dacr', id: 'root', path: '/' },
  ])

  const currentFolder = computed(() => path.value.at(-1)!)
  const currentFolderName = computed(() => currentFolder.value.name)
  const isRootFolder = computed(() => currentFolder.value.path === '/')
  const breadcrumbPath = computed(() => path.value.map((p) => p.name))
  const breadcrumbKey = computed(() => breadcrumbPath.value.join('-'))
  const breadcrumbItems = computed<BreadcrumbItem[]>(() =>
    path.value.map((p) => ({ label: p.name }))
  )

  const folders = computed(
    () =>
      result.value?.folderByPath?.items?.edges
        ?.map((e) => e.node as Folder)
        .filter(Boolean) ?? []
  )

  const shouldDisableFolder = (folder: Folder) =>
    loading.value || moveItemsLoading.value || ids.includes(folder.id)

  const disableAction = computed(() => loading.value || moveItemsLoading.value)

  const handleBreadcrumbSelect = (_item: BreadcrumbItem, index: number) => {
    path.value = path.value.slice(0, index + 1)
    folderPath.value = currentFolder.value.path
  }

  const handleBack = () => {
    path.value.pop()
    folderPath.value = currentFolder.value.path
  }

  const handleFolderClick = (folder: Folder) => {
    if (shouldDisableFolder(folder)) return
    path.value.push({ name: folder.name, id: folder.id, path: folder.path })
    folderPath.value = folder.path
  }

  const handleNewFolderClick = () => {
    const destId = result.value?.folderByPath?.id || 'root'
    openDialog(Dialog.AddFolder, { props: { folderId: destId }, stack: true })
  }

  const save = async () => {
    const destId = result.value?.folderByPath?.id || currentFolder.value.id

    try {
      const response = await moveItems({ sourceIds: ids, to: destId })

      removeItemsFromExplorerCache(ids)
      clearSelection()

      if (response?.data?.moveItems.asyncProcess) {
        openModal(Modals.Success, {
          props: {
            title: 'Bulk change successful',
            html: 'Note: In some cases, it may take several minutes to process your request.',
          },
        })
      } else {
        close()
      }
    } catch (error) {
      console.error(error)

      if (error instanceof Error) {
        openModal(Modals.Error, {
          props: {
            content: 'Could not move items to specified destination',
            error,
          },
        })
      }
    }
  }

  const handleConfirm = async () => {
    if (ids.length >= CONFIRM_THRESHOLD) {
      openModal(Modals.ConfirmMove, {
        props: { folder: result.value?.folderByPath, ids, callback: save },
      })
      return
    }

    await save()
  }

  return {
    loading,
    moveItemsLoading,
    folders,
    currentFolderName,
    isRootFolder,
    breadcrumbItems,
    breadcrumbKey,
    disableAction,
    shouldDisableFolder,
    handleFolderClick,
    handleBack,
    handleBreadcrumbSelect,
    handleNewFolderClick,
    handleConfirm,
  }
}
